// Browser-only helpers for the free image tools. Every function here runs inside
// event handlers or effects, never during server rendering.

export type OutputFormat = 'jpg' | 'png' | 'webp'

export type Rect = {
    x: number
    y: number
    width: number
    height: number
}

export type ResizeMode = 'fit' | 'crop' | 'stretch'

export const MIME: Record<OutputFormat, string> = {
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp'
}

export const FORMAT_LABEL: Record<OutputFormat, string> = {
    jpg: 'JPG',
    png: 'PNG',
    webp: 'WebP'
}

// Safari refuses to draw canvases larger than 4096 x 4096 pixels.
export const MAX_PIXELS = 16_777_216

export const IMAGE_ACCEPT = 'image/*,.heic,.heif'

export const formatBytes = (bytes: number): string => {
    if (bytes < 1000) return `${bytes} B`
    if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(bytes < 10_000 ? 1 : 0)} KB`
    return `${(bytes / 1_000_000).toFixed(2)} MB`
}

export const fileStem = (name: string): string => name.replace(/\.[^./\\]+$/, '') || 'image'

export const formatFromType = (type: string): OutputFormat | null => {
    if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg'
    if (type === 'image/png') return 'png'
    if (type === 'image/webp') return 'webp'
    return null
}

export const isHeic = (file: File): boolean => /\.(heic|heif)$/i.test(file.name) || /image\/hei[cf]/i.test(file.type)

export const loadImage = (blob: Blob): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()

    img.onload = () => {
        resolve(img)
        URL.revokeObjectURL(url)
    }
    img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('This file could not be opened as an image.'))
    }
    img.src = url
})

export const decodeHeic = async (file: Blob): Promise<Blob> => {
    const { default: heic2any } = await import('heic2any')
    const output = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 })

    return Array.isArray(output) ? output[0] : output
}

// Opens any image the browser can read, including HEIC photos from iPhones.
// Returns the decoded element plus a blob the browser can display.
export const openImage = async (file: File): Promise<{ img: HTMLImageElement, blob: Blob }> => {
    if (!isHeic(file)) return { img: await loadImage(file), blob: file }

    try {
        return { img: await loadImage(file), blob: file }
    }
    catch {
        try {
            const jpeg = await decodeHeic(file)
            return { img: await loadImage(jpeg), blob: jpeg }
        }
        catch {
            throw new Error('This HEIC photo could not be opened. Try exporting it as JPG from your phone first.')
        }
    }
}

const createCanvas = (width: number, height: number): HTMLCanvasElement => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    return canvas
}

const context = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Your browser could not prepare this image.')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    return ctx
}

interface RenderOptions {
    width: number
    height: number
    source?: Rect
    dest?: Rect
    background?: string
}

// Draws an image (or part of it) onto a new canvas. Large reductions are done in
// halving steps, which keeps edges and text noticeably sharper.
export const renderImage = (img: HTMLImageElement, options: RenderOptions): HTMLCanvasElement => {
    const width = Math.max(1, Math.round(options.width))
    const height = Math.max(1, Math.round(options.height))

    if (width * height > MAX_PIXELS) {
        throw new Error('That size is too large for your browser to handle. Try smaller dimensions.')
    }

    const dest = options.dest ?? { x: 0, y: 0, width, height }
    let rect: Rect = options.source ?? { x: 0, y: 0, width: img.naturalWidth, height: img.naturalHeight }
    let current: CanvasImageSource = img

    while (rect.width >= dest.width * 2 && rect.height >= dest.height * 2) {
        const stepWidth = Math.round(rect.width / 2)
        const stepHeight = Math.round(rect.height / 2)
        const step = createCanvas(stepWidth, stepHeight)
        context(step).drawImage(current, rect.x, rect.y, rect.width, rect.height, 0, 0, stepWidth, stepHeight)
        current = step
        rect = { x: 0, y: 0, width: stepWidth, height: stepHeight }
    }

    const canvas = createCanvas(width, height)
    const ctx = context(canvas)

    if (options.background) {
        ctx.fillStyle = options.background
        ctx.fillRect(0, 0, width, height)
    }

    ctx.drawImage(current, rect.x, rect.y, rect.width, rect.height, dest.x, dest.y, dest.width, dest.height)
    return canvas
}

export const encodeCanvas = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (!blob) {
            reject(new Error('Your browser could not save this image. Try a smaller size or a different format.'))
            return
        }
        if (blob.type !== type) {
            reject(new Error('Your browser cannot create this file type. Try JPG or PNG instead.'))
            return
        }
        resolve(blob)
    }, type, quality)
})

// Works out the output size and the part of the source to draw for each resize mode.
export const planResize = (
    sourceWidth: number,
    sourceHeight: number,
    targetWidth: number | null,
    targetHeight: number | null,
    mode: ResizeMode
): { width: number, height: number, source: Rect } | null => {
    const full: Rect = { x: 0, y: 0, width: sourceWidth, height: sourceHeight }

    if (!targetWidth && !targetHeight) return null

    if (mode === 'fit' || !targetWidth || !targetHeight) {
        const scales = [
            targetWidth ? targetWidth / sourceWidth : Infinity,
            targetHeight ? targetHeight / sourceHeight : Infinity
        ]
        const scale = Math.min(...scales)

        return {
            width: Math.max(1, Math.round(sourceWidth * scale)),
            height: Math.max(1, Math.round(sourceHeight * scale)),
            source: full
        }
    }

    if (mode === 'stretch') return { width: targetWidth, height: targetHeight, source: full }

    const targetRatio = targetWidth / targetHeight
    const sourceRatio = sourceWidth / sourceHeight

    if (sourceRatio > targetRatio) {
        const cropWidth = sourceHeight * targetRatio
        return {
            width: targetWidth,
            height: targetHeight,
            source: { x: (sourceWidth - cropWidth) / 2, y: 0, width: cropWidth, height: sourceHeight }
        }
    }

    const cropHeight = sourceWidth / targetRatio
    return {
        width: targetWidth,
        height: targetHeight,
        source: { x: 0, y: (sourceHeight - cropHeight) / 2, width: sourceWidth, height: cropHeight }
    }
}

export const resizeImage = async (
    img: HTMLImageElement,
    targetWidth: number | null,
    targetHeight: number | null,
    mode: ResizeMode,
    format: OutputFormat
): Promise<{ blob: Blob, width: number, height: number }> => {
    const plan = planResize(img.naturalWidth, img.naturalHeight, targetWidth, targetHeight, mode)
    if (!plan) throw new Error('Enter a width, a height, or both.')

    const canvas = renderImage(img, {
        width: plan.width,
        height: plan.height,
        source: plan.source,
        background: format === 'jpg' ? '#ffffff' : undefined
    })
    const blob = await encodeCanvas(canvas, MIME[format], format === 'png' ? undefined : 0.92)

    return { blob, width: plan.width, height: plan.height }
}

const MIN_QUALITY = 0.4

// Finds the best looking file at or under the target size. When even a
// modest quality is too big, it reduces the dimensions and tries again.
export const compressToTarget = async (
    img: HTMLImageElement,
    targetBytes: number,
    format: 'jpg' | 'webp'
): Promise<{ blob: Blob, width: number, height: number, quality: number }> => {
    const type = MIME[format]
    const naturalWidth = img.naturalWidth
    const naturalHeight = img.naturalHeight
    let scale = Math.min(1, Math.sqrt(MAX_PIXELS / (naturalWidth * naturalHeight)))

    for (let attempt = 0; attempt < 10; attempt++) {
        const width = Math.max(1, Math.round(naturalWidth * scale))
        const height = Math.max(1, Math.round(naturalHeight * scale))

        if (width < 16 || height < 16) break

        const canvas = renderImage(img, { width, height, background: format === 'jpg' ? '#ffffff' : undefined })
        const top = await encodeCanvas(canvas, type, 0.92)
        if (top.size <= targetBytes) return { blob: top, width, height, quality: 0.92 }

        const floor = await encodeCanvas(canvas, type, MIN_QUALITY)

        if (floor.size <= targetBytes) {
            let low = MIN_QUALITY
            let high = 0.92
            let best = floor
            let bestQuality = MIN_QUALITY

            for (let i = 0; i < 6; i++) {
                const quality = (low + high) / 2
                const blob = await encodeCanvas(canvas, type, quality)

                if (blob.size <= targetBytes) {
                    best = blob
                    bestQuality = quality
                    low = quality
                }
                else {
                    high = quality
                }
            }

            return { blob: best, width, height, quality: bestQuality }
        }

        scale *= Math.max(0.35, Math.min(0.92, Math.sqrt(targetBytes / floor.size) * 0.95))
    }

    throw new Error('This image could not get under that target. Try a larger target size.')
}

export const downloadBlob = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export const zipFiles = async (files: { name: string, blob: Blob }[]): Promise<Blob> => {
    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const used = new Map<string, number>()

    for (const file of files) {
        const count = used.get(file.name) ?? 0
        used.set(file.name, count + 1)
        const name = count === 0 ? file.name : file.name.replace(/(\.[^.]+)?$/, `-${count + 1}$1`)
        zip.file(name, file.blob)
    }

    return zip.generateAsync({ type: 'blob' })
}

// ---------------------------------------------------------------------------
// Metadata removal
// ---------------------------------------------------------------------------

const startsWithText = (bytes: Uint8Array, offset: number, text: string): boolean => {
    for (let i = 0; i < text.length; i++) {
        if (bytes[offset + i] !== text.charCodeAt(i)) return false
    }
    return true
}

interface ExifSummary {
    found: string[]
    orientation: number
}

const readExif = (tiff: Uint8Array): ExifSummary => {
    const found: string[] = []
    let orientation = 1

    try {
        const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength)
        const little = view.getUint16(0) === 0x4949
        const ifd = view.getUint32(4, little)
        const count = view.getUint16(ifd, little)
        const tags = new Set<number>()

        for (let i = 0; i < count; i++) {
            const entry = ifd + 2 + i * 12
            if (entry + 12 > tiff.byteLength) break
            const tag = view.getUint16(entry, little)
            tags.add(tag)
            if (tag === 0x0112) orientation = view.getUint16(entry + 8, little)
        }

        if (tags.has(0x010F) || tags.has(0x0110)) found.push('Camera or phone model')
        if (tags.has(0x8825)) found.push('Location (GPS coordinates)')
        if (tags.has(0x0132) || tags.has(0x8769)) found.push('Date, time and camera settings')
        if (tags.has(0x0131)) found.push('Editing software')
        if (tags.has(0x013B) || tags.has(0x8298)) found.push('Author or copyright notes')
    }
    catch {
        // An unreadable EXIF block is still removed below.
    }

    if (found.length === 0) found.push('EXIF details')
    return { found, orientation }
}

interface StripResult {
    blob: Blob
    found: string[]
    resaved: boolean
    note: string
}

const stripJpeg = (bytes: Uint8Array): { parts: Uint8Array[], found: string[], orientation: number } | null => {
    if (bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null

    const parts: Uint8Array[] = [bytes.subarray(0, 2)]
    const found = new Set<string>()
    let orientation = 1
    let i = 2

    while (i < bytes.length - 1) {
        if (bytes[i] !== 0xFF) return null
        const marker = bytes[i + 1]

        if (marker === 0xFF) {
            i += 1
            continue
        }
        if (marker === 0xDA) {
            parts.push(bytes.subarray(i))
            return { parts, found: [...found], orientation }
        }
        if (marker === 0x01 || (marker >= 0xD0 && marker <= 0xD8)) {
            parts.push(bytes.subarray(i, i + 2))
            i += 2
            continue
        }

        const length = (bytes[i + 2] << 8) | bytes[i + 3]
        const end = i + 2 + length
        if (end > bytes.length) return null

        const isIcc = marker === 0xE2 && startsWithText(bytes, i + 4, 'ICC_PROFILE')
        const isMetadata = (marker >= 0xE1 && marker <= 0xEF && !isIcc) || marker === 0xFE

        if (isMetadata) {
            if (marker === 0xE1 && startsWithText(bytes, i + 4, 'Exif')) {
                const exif = readExif(bytes.subarray(i + 10, end))
                exif.found.forEach((item) => found.add(item))
                orientation = exif.orientation
            }
            else if (marker === 0xE1) found.add('XMP details (descriptions, editing history)')
            else if (marker === 0xED) found.add('IPTC details (captions, credits)')
            else if (marker === 0xFE) found.add('Comments')
            else found.add('Other app data')
        }
        else {
            parts.push(bytes.subarray(i, end))
        }

        i = end
    }

    return null
}

const PNG_TEXT_CHUNKS: Record<string, string> = {
    tEXt: 'Text notes',
    zTXt: 'Text notes',
    iTXt: 'Text notes',
    eXIf: 'EXIF details',
    tIME: 'Last edited time'
}

const stripPng = (bytes: Uint8Array): { parts: Uint8Array[], found: string[] } | null => {
    if (!startsWithText(bytes, 1, 'PNG')) return null

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const parts: Uint8Array[] = [bytes.subarray(0, 8)]
    const found = new Set<string>()
    let i = 8

    while (i + 8 <= bytes.length) {
        const length = view.getUint32(i)
        const type = String.fromCharCode(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7])
        const end = i + 12 + length
        if (end > bytes.length) return null

        if (PNG_TEXT_CHUNKS[type]) found.add(PNG_TEXT_CHUNKS[type])
        else parts.push(bytes.subarray(i, end))

        i = end
        if (type === 'IEND') break
    }

    return { parts, found: [...found] }
}

const resave = async (file: File): Promise<Blob> => {
    const { img } = await openImage(file)
    const original = formatFromType(file.type)
    const format: OutputFormat = original === 'webp' ? 'webp' : original === 'png' ? 'png' : 'jpg'
    const canvas = renderImage(img, {
        width: img.naturalWidth,
        height: img.naturalHeight,
        background: format === 'jpg' ? '#ffffff' : undefined
    })

    return encodeCanvas(canvas, MIME[format], format === 'png' ? undefined : 0.95)
}

export const stripMetadata = async (file: File): Promise<StripResult> => {
    const bytes = new Uint8Array(await file.arrayBuffer())
    const type = formatFromType(file.type)

    if (type === 'jpg' || (bytes[0] === 0xFF && bytes[1] === 0xD8)) {
        const result = stripJpeg(bytes)

        if (result && result.orientation > 1) {
            return {
                blob: await resave(file),
                found: result.found,
                resaved: true,
                note: 'Saved as a new high quality copy so the photo stays the right way up.'
            }
        }
        if (result) {
            return {
                blob: new Blob(result.parts as BlobPart[], { type: 'image/jpeg' }),
                found: result.found,
                resaved: false,
                note: 'The picture itself was not re-compressed, so quality is unchanged.'
            }
        }
    }

    if (type === 'png' || startsWithText(bytes, 1, 'PNG')) {
        const result = stripPng(bytes)

        if (result) {
            return {
                blob: new Blob(result.parts as BlobPart[], { type: 'image/png' }),
                found: result.found,
                resaved: false,
                note: 'The picture itself was not changed, so quality is unchanged.'
            }
        }
    }

    return {
        blob: await resave(file),
        found: ['Any stored details in this file'],
        resaved: true,
        note: isHeic(file)
            ? 'Saved as a new JPG copy, which leaves stored details behind.'
            : 'Saved as a new copy, which leaves stored details behind.'
    }
}

export const extensionForBlob = (blob: Blob): string => {
    const format = formatFromType(blob.type)
    return format ?? 'png'
}
